import { Button, Card, Flex, Skeleton } from '@radix-ui/themes';
import { BreakCanvas } from 'classes/break-canvas';
import { CanvasHelper } from 'classes/helpers/canvas.helper';
import { CustomIcon } from 'components/common/custom-icon';
import { ErrorBoundary } from 'components/common/error-boundary';
import { PlotHeader } from 'components/sections/admin-portal/machines/dialogs/training-performance/plots/plot-header';
import useTrainingPerformanceStore from 'components/sections/admin-portal/machines/dialogs/training-performance/store/use-training-performance-store';
import { CustomIconPath } from 'enums/custom.enums';
import { meanObject } from 'lib_ts/classes/math.utilities';
import { TrajHelper } from 'lib_ts/classes/trajectory.helper';
import { BallType } from 'lib_ts/enums/machine.enums';
import {
  MAX_BREAKS_DELTA_IN,
  MAX_BREAKS_DELTA_IN_TRAJEKT_36,
} from 'lib_ts/enums/pitch-performance';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { ITrajektRefBreak } from 'lib_ts/interfaces/pitches';
import { IItrLogExt } from 'lib_ts/interfaces/training/i-training-log';
import { useEffect, useMemo, useRef, useState } from 'react';
import useMiscStore from 'stores/use-misc-store';

const REDRAW_DEQUEUE_MS = 100;

const SKIP_TARGET_CROSSHAIRS = true;

const TARGET_SIZE_PX = 15;

export const TrainingPerfPlotBreaks = (props: { iterations: IItrLogExt[] }) => {
  const loading = useTrainingPerformanceStore((s) => s.loading);

  const selectedLog = useTrainingPerformanceStore((s) => s.selectedLog);
  const selectedShotIds = useTrainingPerformanceStore((s) => s.selectedShotIds);

  const filters = useTrainingPerformanceStore((s) => s.filters);

  const showTargets = useTrainingPerformanceStore((s) => s.showTargets);
  const showThresholds = useTrainingPerformanceStore((s) => s.showThresholds);

  const logShots = useTrainingPerformanceStore((s) => s.logShots);

  const [isAveraged, setIsAveraged] = useState(true);

  const isDarkMode = useMiscStore((s) => s.isDarkMode);

  const canvasDef = useMemo(
    () => BreakCanvas.makeBigSquare(isDarkMode),
    [isDarkMode]
  );

  // what is used for assembly of the results
  const mergeCanvas = useRef<HTMLCanvasElement>(null);
  const rulersCanvas = useRef<HTMLCanvasElement>(null);
  const gridCanvas = useRef<HTMLCanvasElement>(null);
  const shotsCanvas = useRef<HTMLCanvasElement>(null);

  // what gets seen by users + LogRocket
  const [imgSrc, setImgSrc] = useState<string>();
  // for dequeuing updates to the imgSrc
  const [lastUpdate, setLastUpdate] = useState(Date.now());
  const updateTimeout = useRef<NodeJS.Timeout>();

  const MAX_BREAKS_DELTA_INCHES =
    filters.ball_type === BallType.Trajekt_36
      ? MAX_BREAKS_DELTA_IN_TRAJEKT_36
      : MAX_BREAKS_DELTA_IN;

  // aggregate updates from various canvases before updating imgSrc
  useEffect(() => {
    updateTimeout.current = setTimeout(async () => {
      const canvas = mergeCanvas.current;

      if (!canvas) {
        return;
      }

      // all the canvases that need to be combined should go here
      const sources = [
        gridCanvas.current,
        rulersCanvas.current,
        shotsCanvas.current,
      ].filter((c) => c) as HTMLCanvasElement[];

      const src = await CanvasHelper.combineToPNG({
        sources: sources,
        target: canvas,
      });

      setImgSrc(src);
    }, REDRAW_DEQUEUE_MS);

    return () => clearTimeout(updateTimeout.current);
  }, [lastUpdate]);

  // drawRulers and drawGrid
  useEffect(() => {
    async function drawRulers() {
      try {
        const canvas = rulersCanvas.current;
        if (!canvas) {
          return;
        }

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          return;
        }

        ctx.clearRect(0, 0, canvas.width, canvas.height);

        canvasDef.drawPitchReportRulersInches(ctx);
      } catch (e) {
        console.error(e);
      } finally {
        setLastUpdate(Date.now());
      }
    }

    async function drawGrid() {
      try {
        const canvas = gridCanvas.current;
        if (!canvas) {
          return;
        }

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          return;
        }

        ctx.clearRect(0, 0, canvas.width, canvas.height);
        canvasDef.drawGrid(ctx, 10, true);
      } catch (e) {
        console.error(e);
      } finally {
        setLastUpdate(Date.now());
      }
    }

    drawGrid();
    drawRulers();
  }, [loading]);

  // drawBreaks
  useEffect(() => {
    const callback = async () => {
      try {
        const canvas = shotsCanvas.current;
        if (!canvas) {
          return;
        }

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          return;
        }

        ctx.clearRect(0, 0, canvas.width, canvas.height);

        if (!selectedLog) {
          // nothing selected
          return;
        }

        if (!selectedLog.breaksTarget) {
          // selected log has no breaks
          return;
        }

        const targetValue: ITrajektRefBreak = {
          xInches: -1 * selectedLog.breaksTarget.breakXIn,
          zInches: selectedLog.breaksTarget.breakZIn,
        };

        for (const itr of props.iterations) {
          const drawTarget = async () => {
            if (SKIP_TARGET_CROSSHAIRS) {
              return;
            }

            await canvasDef.drawCrosshairs(ctx, {
              as: 'crosshairs',
              value: targetValue,
              color: `rgba(${itr.rgb},100%)`,
              width_px: TARGET_SIZE_PX,
            });
          };

          const itrShots = logShots.filter((s) => itr.shotIDs.includes(s._id));

          // todo: do we skip this iteration entirely if there were no shots?

          // draw acceptable bubble around target
          if (showThresholds) {
            canvasDef.drawDot(ctx, {
              value: targetValue,
              radiusIn: MAX_BREAKS_DELTA_INCHES,
              fillColor: `rgba(${itr.rgb},25%)`,
              borderColor: `rgba(${itr.rgb},75%)`,
              borderWidth: 2,
            });
          }

          if (isAveraged) {
            const avgBreaks = meanObject(
              itrShots.map((s) => TrajHelper.getBreaksFromShot(s))
            );

            if (avgBreaks) {
              const avgValue: ITrajektRefBreak = {
                xInches: -1 * avgBreaks.xInches,
                zInches: avgBreaks.zInches,
              };

              // draw line and label from avg to target
              if (showTargets) {
                canvasDef.drawLine(ctx, {
                  from: avgValue,
                  to: targetValue,
                  lineColor: `rgba(${itr.rgb},100%)`,
                });

                canvasDef.drawLabel(ctx, {
                  value: avgValue,
                  target: targetValue,
                  fontColor: `rgba(${itr.rgb},100%)`,
                });
              }

              // draw avg
              canvasDef.drawDot(ctx, {
                value: avgValue,
                radiusIn: 0.5,
                fillColor: `rgba(${itr.rgb},75%)`,
                borderColor: `rgba(${itr.rgb},100%)`,
                borderWidth: 1,
              });
            }

            if (showTargets) {
              await drawTarget();
            }

            // move onto the next pitch w/o interacting w/ individual shots while averaging
            continue;
          }

          // todo: will we ever label individual shots?
          const showLabels = false;

          for (const shot of itrShots) {
            if (!shot.traj) {
              continue;
            }

            // while something is selected but not this shot, render it in a more subdued styling
            const isSecondary =
              selectedShotIds.length > 0 && !selectedShotIds.includes(shot._id);

            const style = CanvasHelper.getDotStyle(itr.rgb, isSecondary);

            const actual = TrajHelper.getBreaksFromShot(shot);

            const value: ITrajektRefBreak = {
              xInches: -1 * actual.xInches,
              zInches: actual.zInches,
            };

            // draw line and label from shot to target, if not secondary
            if (showLabels && !isSecondary) {
              canvasDef.drawLine(ctx, {
                from: value,
                to: targetValue,
                lineColor: `rgba(${style.rgb},${style.lineOpacity})`,
              });

              canvasDef.drawLabel(ctx, {
                value: value,
                target: targetValue,
                fontColor: `rgba(${style.rgb},${style.lineOpacity})`,
              });
            }

            // draw shot
            canvasDef.drawDot(ctx, {
              value: value,
              radiusIn: 0.5,
              fillColor: `rgba(${style.rgb},${style.fillOpacity})`,
              borderColor: `rgba(${style.rgb},${style.lineOpacity})`,
              borderWidth: 1,
            });
          }

          if (showTargets) {
            await drawTarget();
          }
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLastUpdate(Date.now());
      }
    };

    callback();
  }, [
    props.iterations,
    isAveraged,
    showTargets,
    showThresholds,
    logShots,
    selectedShotIds,
  ]);

  return (
    <ErrorBoundary identifier="TrainingPerfPlotBreaks">
      <Skeleton loading={loading}>
        <Flex direction="column" gap="3" align="stretch">
          <PlotHeader
            title="Break"
            subtitle="Scatter plot of breaks (in) with Hz and Vt components"
          >
            <Flex gap="2" align="center">
              <Button
                size="1"
                variant="soft"
                color={isAveraged ? RADIX.COLOR.ACCENT : RADIX.COLOR.NEUTRAL}
                onClick={() => setIsAveraged((prev) => !prev)}
              >
                Averages{' '}
                <CustomIcon
                  icon={
                    isAveraged
                      ? CustomIconPath.SwitchOn
                      : CustomIconPath.SwitchOff
                  }
                />
              </Button>
            </Flex>
          </PlotHeader>

          <Card className="no-padding">
            <div className="slider">
              <div
                className="sizer"
                style={{
                  aspectRatio: canvasDef.CONFIG.canvas.aspectRatio,
                }}
              >
                {/* for log rocket recordings to show the results */}
                {imgSrc && (
                  <img
                    alt="break performance plot"
                    className="image-wrapper"
                    src={imgSrc}
                  />
                )}
                <canvas
                  ref={mergeCanvas}
                  data-identifier="merge-canvas"
                  width={canvasDef.CONFIG.canvas.width_px}
                  height={canvasDef.CONFIG.canvas.height_px}
                />
                <canvas
                  ref={rulersCanvas}
                  data-identifier="rulers-canvas"
                  width={canvasDef.CONFIG.canvas.width_px}
                  height={canvasDef.CONFIG.canvas.height_px}
                />
                <canvas
                  ref={gridCanvas}
                  data-identifier="grid-canvas"
                  width={canvasDef.CONFIG.canvas.width_px}
                  height={canvasDef.CONFIG.canvas.height_px}
                />
                <canvas
                  ref={shotsCanvas}
                  data-identifier="shots-canvas"
                  width={canvasDef.CONFIG.canvas.width_px}
                  height={canvasDef.CONFIG.canvas.height_px}
                />
              </div>
            </div>
          </Card>
        </Flex>
      </Skeleton>
    </ErrorBoundary>
  );
};
